import {
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { Formik } from "formik";
import React, { useState } from "react";

import MDBox from "@/components/atoms/MDBox";
import { MDButton } from "@/components/atoms/MDButton";
import MDTypography from "@/components/atoms/MDTypography";
import SWR_KEYS from "@/constants/swr-keys";
import { buildActivationWorkflowTypeSelectOptions } from "@/helpers/workflow-type-display";
import useGetAllWorkflowTypes from "@/hooks/requests/wokflow/use-get-all-workflow-types";
import useSaveWorkflow from "@/hooks/requests/wokflow/use-save-workflow";
import useMutate from "@/hooks/use-mutate";
import { IWorkflow } from "@/interfaces/workflow.interface";
import { WorkflowSaveDialogMessages } from "@/messages/workflow/workflow-save-dialog.messages";

import { ACTIVE_WORKFLOW_OPTIONS } from "./constants";
import {
  buildWorkflowActivationFormSchema,
  buildWorkflowActivationInitialData,
  normalizeFormData,
} from "./form-data";
import { IWorkFlowFormData } from "./types";

interface WorkflowActivationFormProps {
  onClose: () => void;
  messages: WorkflowSaveDialogMessages;
  workflows: IWorkflow[];
}

const WorkflowActivationForm = ({ messages, onClose, workflows }: WorkflowActivationFormProps) => {
  const [selectedTriggerType, setSelectedTriggerType] = useState<number>(-1);
  const { mutate } = useMutate();
  const { save, isLoading, loadingIcon } = useSaveWorkflow();
  const { data: workflowTypesData } = useGetAllWorkflowTypes();

  const {
    workflowIdLabel,
    triggerLabel,
    descriptionLabel,
    activateWorkflowButton,
    workflowSavedSuccess,
    workflowSaveError,
  } = messages;

  const handleSubmitForm = async (_formData: IWorkFlowFormData) => {
    const formData = normalizeFormData(_formData);

    const successMessage = workflowSavedSuccess;
    const errorMessage = workflowSaveError;

    const onSuccess = () => {
      void mutate(SWR_KEYS.getWorkflows);
      void mutate(SWR_KEYS.getAllWorkflowTypes);
      onClose();
    };
    const onError = () => onClose();

    save({ formData, successMessage, errorMessage, onSuccess, onError });
  };

  const workflowTypesOptions = buildActivationWorkflowTypeSelectOptions(
    messages,
    workflowTypesData?.items
  );
  const workflowOptions = ACTIVE_WORKFLOW_OPTIONS(workflows, selectedTriggerType, messages);

  return (
    <Formik
      initialValues={buildWorkflowActivationInitialData()}
      validationSchema={buildWorkflowActivationFormSchema(messages)}
      validateOnChange={false}
      onSubmit={handleSubmitForm}
    >
      {(formik) => {
        const { values, handleChange, setFieldValue, errors } = formik;

        const handleNameChange = (event: SelectChangeEvent<number>) => {
          const selectedId = parseInt(event.target.value as string, 10);
          const selectedWorkflow = workflows.find((w) => w.id === selectedId);

          if (selectedWorkflow) {
            setFieldValue("workflowId", selectedId);
            setFieldValue("description", selectedWorkflow.description);
          }
        };

        const handleWorkflowTypeChange = (event: SelectChangeEvent<number>) => {
          const newValue = event.target.value;
          setFieldValue("workflowTypeId", newValue);
          setSelectedTriggerType(parseInt(newValue as string, 10));
          setFieldValue("workflowId", "");
          setFieldValue("description", "");
        };

        return (
          <MDBox p={2}>
            <Grid container spacing={6}>
              <Grid size={12}>
                <FormControl fullWidth error={!!errors.workflowTypeId}>
                  <InputLabel>{triggerLabel}</InputLabel>
                  <Select
                    label={triggerLabel}
                    name="workflowTypeId"
                    value={values.workflowTypeId as number}
                    onChange={handleWorkflowTypeChange}
                  >
                    {workflowTypesOptions.map((option, index) => (
                      <MenuItem key={index} value={option.value} disabled={option.disabled}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>

                  <FormHelperText>{errors.workflowTypeId}</FormHelperText>
                </FormControl>
              </Grid>
              <Grid size={12}>
                <FormControl fullWidth error={!!errors.workflowId}>
                  <InputLabel>{workflowIdLabel}</InputLabel>
                  <Select
                    label={workflowIdLabel}
                    name="workflowId"
                    disabled={selectedTriggerType === -1}
                    value={values.workflowId}
                    onChange={(e) => handleNameChange(e)}
                  >
                    {workflowOptions.map((option, index) => (
                      <MenuItem key={index} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>{errors.workflowId}</FormHelperText>
                </FormControl>
              </Grid>
              <Grid size={12}>
                <MDTypography variant="caption" color="text" sx={{ display: "block", mb: 0.5 }}>
                  {descriptionLabel}
                </MDTypography>
                <TextField
                  name="description"
                  value={values.description}
                  onChange={handleChange}
                  fullWidth
                  multiline
                  rows={4}
                  error={!!errors.description}
                  helperText={errors.description}
                  disabled
                />
              </Grid>
              <Grid size={12}>
                <MDButton variant="gradient" color="dark" fullWidth onClick={formik.submitForm}>
                  {isLoading ? loadingIcon : activateWorkflowButton}
                </MDButton>
              </Grid>
            </Grid>
          </MDBox>
        );
      }}
    </Formik>
  );
};

export default WorkflowActivationForm;
